import { useParams } from "react-router-dom";
import TitleBar from "../../common/title-bar/title-bar";
import Button from "../../common/button/button";
import PaymentIcon from "../../icons/payment-icon";
import Text from "../../common/typography/text";
import DropdownComponent from "../../common/dropdown-input";
import "./ReportsHome.css";
import Table from "../../common/table";
import TableLink from "../../common/table/table-link";
import { useEffect, useMemo, useState } from "react";
import NewReport from "./NewReport";
import ModalView from "../../common/modal";
import TextInput from "../../common/text-input";
import TickIcon from "../../icons/tick-icon";
import APIService from "../../Service/APIService";
import dayjs from "dayjs";
import { Flex, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { exportToCSV, exportToXLSX } from "../Utils/ExcelExports";
import { useNavigate } from "react-router-dom";
import { useAuth } from '../AuthProvider.js';

const isPreformattedUsDate = (value) =>
  typeof value === "string" && /^\d{2}-\d{2}-\d{4}$/.test(value.trim());

const formatDateForExport = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  if (isPreformattedUsDate(value)) {
    return value.trim();
  }

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("MM-DD-YYYY") : "";
};

const ReportsHome = () => {
  const { user } = useAuth();
  const userData = user;
  const { id } = useParams();
  const navigate = useNavigate();
  const [reportData, setReportData] = useState();
  const [groupByField, setGroupByField] = useState({
    label: "Region",
    value: "Region",
  });
  const [loading, setLoading] = useState(true);
  const [showFilters, setOpenFilters] = useState(false);
  const [saveReport, setSaveReport] = useState(false);
  const [reportName, setReportName] = useState("");
  const [exportOption, setExportOption] = useState("");

  const [currentSales, setCurrentSales] = useState([]);
  const [realizedSales, setRealizedSales] = useState([]);
  const [openBalance, setOpenBalance] = useState([]);
  const [pastDue, setPastDue] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [ogData, setOgData] = useState([]);
  const [reportStatus, setReportStatus] = useState("");

  const exportColumns = [
    { key: "Region", header: "Region" },
    { key: "States", header: "States" },
    { key: "InvoiceNumber", header: "Invoice Number" },
    { key: "InvoiceStatus", header: "Invoice Status" },
    { key: "InvoiceDate", header: "Invoice Date" },
    {
      key: "InvoiceDueDate",
      header: "Invoice Due",
      transform: formatDateForExport,
    },
    { key: "PONumber", header: "PO Number" },
    { key: "OrderType", header: "Order Type" },
    { key: "OrderDate", header: "Order Date" },
    { key: "AccountName", header: "Account" },
    { key: "BillingAccountID", header: "Billing Account ID" },
    { key: "BillingAccountName", header: "Billing Account Name" },
    { key: "DistributorName", header: "Distributor" },
    { key: "ManufacturerName", header: "Manufacturer" },
    { key: "ProductGroupName", header: "Product Group" },
    { key: "TotalUnits", header: "Total Units" },
    { key: "OpenBalance", header: "Open Balance" },
    {
      key: "Total",
      header: "Total",
      transform: (v) => Number(v ?? 0).toFixed(2),
    },
  ];

  const tableHeadersReportsHome = [
    {
      headerName: "Region",
      field: "Region",
      filter: true,
    },
    {
      headerName: "States",
      field: "States",
      filter: true,
    },
    {
      headerName: "Invoice",
      field: "InvoiceNumber",
      filter: true,
    },
    {
      headerName: "Invoice Status",
      field: "InvoiceStatus",
      filter: true,
    },
    {
      headerName: "Invoice Date",
      field: "InvoiceDate",
      filter: true,
    },
    {
      headerName: "Invoice Due",
      field: "InvoiceDueDate",
      filter: true,
      cellRenderer: (params) =>
        params.data?.InvoiceDueDate
          ? dayjs(params.data.InvoiceDueDate).format("MM-DD-YYYY")
          : "",
    },
    {
      headerName: "PO Number",
      field: "PONumber",
      filter: true,
    },
    {
      headerName: "Order Type",
      field: "OrderType",
      filter: true,
    },
    {
      headerName: "Order Date",
      field: "OrderDate",
      filter: true,
    },
    {
      headerName: "Account",
      field: "AccountName",
      filter: true,
      cellRenderer: (params) => (
        <TableLink
          value={params.data.AccountName}
          path={`/Account/${params.data.AccountID}`}
        />
      ),
    },
    {
      headerName: "Distributor",
      field: "DistributorName",
      filter: true,
    },
    {
      headerName: "Manufacturer",
      field: "ManufacturerName",
      filter: true,
    },
    {
      headerName: "Product Group",
      field: "ProductGroupName",
      filter: true,
    },
    {
      headerName: "Order Type",
      field: "OrderType",
      filter: true,
    },
    {
      headerName: "Total",
      field: "Total",
      filter: true,
      cellDataType: "number",
      valueGetter: (params) =>
        parseFloat(String(params?.data?.Total).replace(/[^0-9.-]+/g, "")),
      cellRenderer: (params) => {
        const value = parseFloat(
          String(params?.data?.Total).replace(/[^0-9.-]+/g, "")
        );
        return `$${value.toLocaleString()}`;
      },
    },
    {
      headerName: "Open Balance",
      field: "OpenBalance",
      filter: true,
      cellDataType: "number",
      valueGetter: (params) =>
        parseFloat(String(params?.data?.OpenBalance).replace(/[^0-9.-]+/g, "")),
      cellRenderer: (params) => {
        const value = isNaN(params.data.OpenBalance) ? params.data.OpenBalance : parseFloat(String(params?.data?.OpenBalance ?? 0).replace(/[^0-9.-]+/g, ""));
        console.log("Open Balance Value: ", value);
        return !isNaN(params?.data?.OpenBalance) ? `$${value.toLocaleString()}` : "NA";
      },
    },
  ];
  const   getSalesReport = async () => {
    const serv = new APIService();
    const response = await serv.getDataFromBackend(
      `GetSalesReport?ReportID=${id}`
    );
    return response.data;
  };

  useEffect(() => {
    // dummyReportHome.map((report) => {
    //   setReportData(report.reportData);
    // });
    if (!userData.Reports) {
      navigate("/");
    }
    getSalesReport().then((data) => {
      if(data === null){
        navigate('/');
        return;
      }
      setReportData(data.ReportData);
      console.log("Report Data: ", data.ReportData);
      setOgData(data.ReportData);
      setCurrentSales(data.CurrentSales);
      setRealizedSales(data.RealizedSales);
      setOpenBalance(data.OpenBalance);
      setPastDue(data.PastDue);
      setStartDate(dayjs(data.StartDate).format("MM-DD-YYYY"));
      setEndDate(dayjs(data.EndDate).format("MM-DD-YYYY"));
      setReportName(data.ReportName);
      setReportStatus(data.ReportStatus);
      setLoading(false);
    });
  }, []);

  const formattedTableHeaders = useMemo(() => {
    if (groupByField?.label) {
      return tableHeadersReportsHome?.filter(
        (header) => header.headerName !== groupByField?.label
      );
    } else {
      return tableHeadersReportsHome;
    }
  }, [groupByField?.label]);

  const groupedData = useMemo(() => {
    if (!groupByField?.value || !reportData) return {};

    const keyName = groupByField.value; // e.g., "AccountName"
    return reportData.reduce((acc, item) => {
      const key = item?.[keyName] ?? "—";
      (acc[key] ||= []).push(item);
      return acc;
    }, {});
  }, [groupByField?.value, reportData]);

  const handleShowFilters = () => {
    setOpenFilters(!showFilters);
  };

  const formatNumber = (value) =>
    value !== undefined
      ? value?.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "0.00";

  const downloadReport = (format) => {
    console.log("Downloading report in format:", format);
    const rows = ogData || [];
    if (format === "CSV") {
      exportToCSV(rows, "Sales Report", exportColumns);
    } else if (format === "Excel") {
      exportToXLSX(rows, "Sales Report", exportColumns);
    }
  };

  const saveReportHandler = async () => {
    console.log("Saving report...");
    // Implement your save report logic here
    const serv = new APIService();
    const response = await serv.addDataToBackend(
      { ID: id, ReportName: reportName },
      `UpdateSalesReport?type=Save`
    );
    console.log("Save Report Response: ", response);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
      {loading && (
        <Flex align="center" gap="middle">
          <Spin
            indicator={
              <LoadingOutlined
                style={{
                  fontSize: 48,
                }}
                spin
              />
            }
            fullscreen
          />
        </Flex>
      )}
      <TitleBar
        label="All Sales Report"
        // invoiceNumber={`${startDate} - ${endDate}`}
        invoiceNumber={reportName}
        statusIndicatorStatus={reportStatus}
        statusIndicatorStatusType={
          reportStatus === "Draft" ? "warning" : "success"
        }
        rightFilterBarComponents={
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <Button
              variant="table"
              subVariant="primary"
              onClick={() => setOpenFilters(true)}
            >
              Show Filters
            </Button>
            {reportStatus === "Draft" && (
              <Button
                variant="table"
                subVariant="success"
                icon={<TickIcon />}
                onClick={() => setSaveReport(true)}
              >
                Save Report
              </Button>
            )}
          </div>
        }
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          alignSelf: "stretch",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "32px" }}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "16px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <PaymentIcon className="icon-container" />
              <Text color="lightText">Current Sales</Text>
            </div>
            <Text variant="numberBig">
              ${formatNumber(currentSales)?.split(".")[0]}
              <Text variant="numbersSmall" color="lightText">
                .{formatNumber(currentSales)?.split(".")[1]}
              </Text>
            </Text>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "16px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <PaymentIcon className="icon-container" />
              <Text color="lightText">Realized Sales</Text>
            </div>
            <Text variant="numberBig">
              ${formatNumber(realizedSales)?.split(".")[0]}
              <Text variant="numbersSmall" color="lightText">
                .{formatNumber(realizedSales)?.split(".")[1]}
              </Text>
            </Text>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "16px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <PaymentIcon className="icon-container" />
              <Text color="lightText">Open Balance</Text>
            </div>
            <Text variant="numberBig">
              ${formatNumber(openBalance)?.split(".")[0]}
              <Text variant="numbersSmall" color="lightText">
                .{formatNumber(openBalance)?.split(".")[1]}
              </Text>
            </Text>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "16px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <PaymentIcon className="icon-container" />
              <Text color="lightText">Past Due</Text>
            </div>
            <Text variant="numberBig" color="failureColor">
              ${formatNumber(pastDue)?.split(".")[0]}
              <Text variant="numbersSmall" color="failureColor">
                .{formatNumber(pastDue)?.split(".")[1]}
              </Text>
            </Text>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <DropdownComponent
            label="Group By"
            placeholder=""
            options={[
              { label: "Region", value: "Region" },
              { label: "Account", value: "AccountName" },
              { label: "Distributor", value: "DistributorName" },
              { label: "Manufacturer", value: "ManufacturerName" },
              { label: "Product Group", value: "ProductGroupName" },
              { label: "Order Type", value: "OrderType" },
              { label: "Invoice Status", value: "InvoiceStatus" },
            ]}
            value={groupByField}
            setValue={setGroupByField}
          />
          <DropdownComponent
            label="Export"
            placeholder="Select Format"
            options={[
              { label: "Excel", value: "Excel" },
              { label: "CSV", value: "CSV" },
            ]}
            value={exportOption}
            setValue={setExportOption}
            parentFunc={downloadReport}
            type="ExcelExport"
            // onChange={(e) => downloadReport(e.target.value)}
          />
        </div>
      </div>

      {Object.keys(groupedData)?.length === 0 && (
        <div className="reports-home-no-results-container">
          <Text variant="smallText">
            There are no records for this query. Please try adjusting your
            filters.
          </Text>
        </div>
      )}

      {Object.entries(groupedData).map(([groupName, rows]) => {
        const total = rows.reduce(
          (sum, row) => sum + (Number(row.Total) || 0),
          0
        );
        const [whole, decimal] = formatNumber(total)?.split(".");

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              key={groupName}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                alignSelf: "stretch",
              }}
            >
              <div className="group-filter-title-container">
                <Text variant="smallText">
                  {groupName}{" "}
                  <Text variant="smallText" color="lightText">
                    {rows.length}
                  </Text>
                </Text>
                <Text variant="bodyText">
                  ${whole}
                  <Text variant="bodyText" color="lightText">
                    .{decimal}
                  </Text>
                </Text>
              </div>
            </div>
            <Table
              rowData={rows}
              columnDefs={formattedTableHeaders}
              pagination={false}
            />
          </div>
        );
      })}

      {showFilters && (
        <NewReport
          handleRunNewReport={handleShowFilters}
          openRunNewReport={showFilters}
          reportID={id}
        />
      )}

      {saveReport && (
        <ModalView
          isOpen={saveReport}
          onClose={() => setSaveReport(false)}
          modalTitle="Save Report"
          textInputComponent={
            <div className="field-group-wrapper">
              <div className="field-group">
                <TextInput
                  titleText="Report Name"
                  placeholder="Report Name"
                  parentClassName="full-width-button"
                  value={reportName}
                  isRequired
                  setValue={setReportName}
                />
              </div>
            </div>
          }
          footerButtonWrapper={
            <Button
              variant="table"
              subVariant="success"
              onClick={saveReportHandler}
            >
              Save
            </Button>
          }
        />
      )}
    </div>
  );
};

export default ReportsHome;
